import {
  BorderRadius,
  LightModeSemanticColors,
  fontSizes,
} from '@enechain/ecloud-designsystem'

/** ラベル span の共通 style（design 準拠: font-size-md / 太字 / mid-emphasis）。 */
export const statusHeaderLabelStyle: React.CSSProperties = {
  fontSize: fontSizes.md.value,
  fontWeight: 600,
  color: LightModeSemanticColors.Text.midEmphasis.value,
  whiteSpace: 'nowrap',
}

/** 値 span の共通 style（design 準拠: font-size-md / 標準 / high-emphasis）。 */
export const statusHeaderValueStyle: React.CSSProperties = {
  fontSize: fontSizes.md.value,
  fontWeight: 400,
  color: LightModeSemanticColors.Text.highEmphasis.value,
  fontVariantNumeric: 'tabular-nums',
}

export type StatusHeaderItem = {
  /** React key（items 配列の並び順キー）。 */
  key: string
  label: React.ReactNode
  value: React.ReactNode
}

type Props = {
  /** label + value の項目群（ステータス badge / コード / バージョン / 日時 等）。 */
  items: StatusHeaderItem[]
  /**
   * 主操作ボタン等の action slot（例: 提出ボタン）。
   * 指定時のみ items を内側 div でラップし、card を justify-content: space-between
   * にして右端に action を配置する（GenerationSalesPlanDetail 相当）。 action は折り返さず
   * 右端に固定し、 項目側だけを折り返す (#11104)。
   * 未指定時は items を card 直下にそのまま並べる（ProcurementPlanDetail のように
   * action の無いページの DOM 構造を維持する）。
   */
  action?: React.ReactNode
}

const renderItem = (item: StatusHeaderItem): React.ReactElement => (
  <span
    key={item.key}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      minWidth: 0,
    }}
  >
    <span style={statusHeaderLabelStyle}>{item.label}</span>
    {item.value}
  </span>
)

/**
 * 計画提出詳細ページ共通のステータスヘッダーカード（design `ds-submit-meta` 相当）。
 *
 * design 準拠: 白背景（surface-primary）+ border + radius のカード型バー。
 * ラベルは font-size-md / 太字 / mid-emphasis、値は font-size-md / 標準 / high-emphasis。
 *
 * GenerationSalesPlanDetail / ProcurementPlanDetail はいずれも action = 提出
 * ボタンを渡す（両画面とも手動提出動線を持つ）。action を渡さないページ
 * （action なしの DOM 構造）も引き続きサポートする。DetailHeader が共有する
 * 構造を吸収する。ページ固有の項目定義・値整形（submissionEntityCode/bgCode
 * の複合表示、timestampText 等）は呼び出し元が `items` として組み立てる。
 */
export const StatusHeaderCard = ({
  items,
  action,
}: Props): React.ReactElement => {
  const cardStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: action !== undefined ? 'space-between' : undefined,
    columnGap: '20px',
    rowGap: '4px',
    minHeight: '52px',
    padding: '0 12px',
    background: LightModeSemanticColors.Surface.primary.value,
    border: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
    borderRadius: BorderRadius.radiiMd.value, // eclair --radius-md (6px)
  }

  if (action === undefined) {
    return <div style={cardStyle}>{items.map(renderItem)}</div>
  }

  // 提出ボタン (action) は右端に固定し、 項目側だけを折り返す (#11104、 2026-10-20 Des 決定)。
  // card ごと折り返すと、 GC 系のバッジ等で項目が幅を使い切った時にボタンが 2 段目の左へ
  // 押し出され、 押す位置が計画ごとに変わる。 項目の器を伸び縮みさせ (flex 1 / minWidth 0)、
  // ボタンの器は縮めない。 項目が 2 段になった時に詰まらないよう上下に 8px 空ける
  return (
    <div style={{ ...cardStyle, flexWrap: 'nowrap', padding: '8px 12px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          flex: '1 1 auto',
          minWidth: 0,
          columnGap: '20px',
          rowGap: '4px',
        }}
      >
        {items.map(renderItem)}
      </div>
      <div style={{ display: 'flex', flexShrink: 0, alignItems: 'center' }}>
        {action}
      </div>
    </div>
  )
}
